/**
 * /admin/ops/snapshots — Tenant Configuration Versioning (SCRUM-320).
 *
 * Historial inmutable de sm_tenant_config por tenant, con diff y rollback
 * selectivo por dominio. No es un backup de DB (SCRUM-314, separado) ni
 * reemplaza Experience Definition (draft/published tienen su propio ciclo).
 * Restaurar nunca reescribe historia: crea una nueva versión "restore".
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { TenantPicker, type TenantPickerOption } from "@/components/admin/TenantPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import {
  TENANT_CONFIG_DOMAIN_LABELS,
  type TenantConfigDomain,
} from "@/lib/tenant-config-versioning";

type SnapshotRow = {
  id: string;
  tenantId: string;
  name: string;
  createdAt: string;
  actorName: string | null;
  origin: "auto" | "manual" | "restore";
  domains: TenantConfigDomain[];
  diffCount: number | null;
  payload: { config?: Record<string, unknown> };
  restoredFromSnapshotId: string | null;
};

const ORIGIN_LABELS: Record<string, string> = { auto: "Automática", manual: "Manual", restore: "Restauración" };
const ORIGIN_COLOR: Record<string, string> = {
  auto: "bg-muted text-muted-foreground",
  manual: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  restore: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

const PAGE_SIZE = 20;

export const Route = createFileRoute("/admin/ops/snapshots")({
  component: SnapshotsPage,
  head: () => ({ meta: [{ title: "Configuración — versionado — Structura Market" }] }),
});

function fmt(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

function SnapshotsPage() {
  const { confirm, prompt } = useAppDialogs();
  const [tenants, setTenants] = useState<TenantPickerOption[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [detail, setDetail] = useState<SnapshotRow | null>(null);
  const [selectedDomains, setSelectedDomains] = useState<Set<TenantConfigDomain>>(new Set());
  const [restoring, setRestoring] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiGetJson<TenantPickerOption[] | { tenants: TenantPickerOption[] }>("/api/admin/tenants")
      .then((r) => setTenants(Array.isArray(r) ? r : (r.tenants ?? [])))
      .catch(() => {});
  }, []);

  const load = useCallback(async (off: number, append: boolean) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ tenantId, limit: String(PAGE_SIZE), offset: String(off) });
      const data = await apiGetJson<{ rows: SnapshotRow[]; hasMore: boolean }>(`/api/admin/config-snapshots?${params}`);
      setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
      setHasMore(data.hasMore);
      setOffset(off);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar versiones");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setRows([]);
    if (tenantId) void load(0, false);
  }, [tenantId, load]);

  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: rows.length,
      todayCount: rows.filter((r) => r.createdAt.startsWith(today)).length,
      lastAt: rows[0]?.createdAt ?? null,
      manual: rows.filter((r) => r.origin === "manual").length,
    };
  }, [rows]);

  const createManual = async () => {
    if (!tenantId) return;
    const reason = await prompt({ title: "Guardar punto de recuperación", description: "Motivo (opcional)", placeholder: "Antes de migrar a plan business" });
    if (reason === null) return;
    setCreating(true);
    try {
      await apiSendJson("/api/admin/config-snapshots", "POST", { tenantId, action: "create_manual", reason: reason || undefined });
      toast.success("Versión creada");
      await load(0, false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear versión");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = (row: SnapshotRow) => {
    setDetail(row);
    setSelectedDomains(new Set(row.domains));
  };

  const restore = async () => {
    if (!detail || selectedDomains.size === 0) return;
    const ok = await confirm({
      title: "Restaurar configuración",
      description: `Se restaurarán ${selectedDomains.size} dominio(s) desde la versión del ${fmt(detail.createdAt)}. Esto crea una nueva versión — no reescribe el historial.`,
      destructive: true,
    });
    if (!ok) return;
    setRestoring(true);
    try {
      await apiSendJson("/api/admin/config-snapshots", "POST", {
        tenantId: detail.tenantId,
        action: "restore",
        snapshotId: detail.id,
        domains: Array.from(selectedDomains),
      });
      toast.success("Configuración restaurada — se creó una nueva versión");
      setDetail(null);
      await load(0, false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al restaurar");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div>
      <h1 className="px-6 pt-5 pb-2 text-lg font-bold text-foreground">Configuración — versionado</h1>
      <div className="p-4 sm:p-6 space-y-4">
        <OpsSubnav hint="Historial de sm_tenant_config por tenant — diff y restore selectivo. No reemplaza backup de DB ni Experience Definition." />

        <TenantPicker tenants={tenants} value={tenantId} onChange={setTenantId} placeholder="Elegir tenant" className="h-9 w-full sm:w-[300px]" />

        {!tenantId ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Elegí un tenant para ver su historial de configuración.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <KpiTile label="Versiones (cargadas)" value={kpis.total} />
              <KpiTile label="Hoy" value={kpis.todayCount} />
              <KpiTile label="Manuales" value={kpis.manual} />
              <div className="rounded-lg border border-border/60 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Última versión</p>
                <p className="text-sm font-medium mt-0.5">{kpis.lastAt ? fmt(kpis.lastAt) : "—"}</p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button size="sm" onClick={() => void createManual()} disabled={creating}>
                {creating ? "Guardando…" : "Guardar punto de recuperación"}
              </Button>
            </div>

            {loading && rows.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Sin versiones registradas todavía para este tenant.</p>
            ) : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => openDetail(r)}
                    className="w-full text-left rounded-xl border p-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{r.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {fmt(r.createdAt)} · {r.actorName ?? "sistema"}
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ORIGIN_COLOR[r.origin]}`}>
                        {ORIGIN_LABELS[r.origin]}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.domains.map((d) => (
                        <span key={d} className="text-[10px] rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                          {TENANT_CONFIG_DOMAIN_LABELS[d]}
                        </span>
                      ))}
                    </div>
                    {r.diffCount != null && (
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {r.diffCount === 0 ? "Sin cambios vs. versión anterior" : `${r.diffCount} campo(s) cambiados vs. versión anterior`}
                      </p>
                    )}
                  </button>
                ))}
                {hasMore && (
                  <Button variant="outline" className="w-full" disabled={loading} onClick={() => void load(offset + PAGE_SIZE, true)}>
                    {loading ? "Cargando…" : "Cargar más"}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {detail ? (
            <>
              <DialogHeader>
                <DialogTitle>{detail.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-xs text-muted-foreground">
                  {fmt(detail.createdAt)} · {detail.actorName ?? "sistema"} · {ORIGIN_LABELS[detail.origin]}
                  {detail.restoredFromSnapshotId ? ` (desde ${detail.restoredFromSnapshotId})` : ""}
                </p>

                <div>
                  <p className="text-xs font-semibold mb-2">Dominios en esta versión — seleccioná cuáles restaurar</p>
                  <div className="flex flex-wrap gap-2">
                    {detail.domains.map((d) => (
                      <label key={d} className="flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDomains.has(d)}
                          onChange={(e) => {
                            setSelectedDomains((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(d); else next.delete(d);
                              return next;
                            });
                          }}
                        />
                        {TENANT_CONFIG_DOMAIN_LABELS[d]}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold mb-1.5">Configuración en esta versión</p>
                  <div className="rounded-lg border divide-y max-h-64 overflow-y-auto">
                    {Object.entries(detail.payload.config ?? {}).map(([k, v]) => (
                      <div key={k} className="flex gap-2 px-2.5 py-1.5 text-xs">
                        <span className="font-mono text-muted-foreground shrink-0 min-w-[160px]">{k}</span>
                        <span className="truncate">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full"
                  variant="destructive"
                  disabled={selectedDomains.size === 0 || restoring}
                  onClick={() => void restore()}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  {restoring ? "Restaurando…" : `Restaurar ${selectedDomains.size} dominio(s)`}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-0.5">{value}</p>
    </div>
  );
}
